/**
 * ภาพรวมการจองวันหยุด (รอบปัจจุบัน) — HR/หัวหน้าดู read-only, จัดกลุ่มตาม "ตำแหน่ง".
 * แต่ละตำแหน่ง: รายชื่อ + วันที่หยุดที่จอง + แถบจำนวนคนหยุดต่อวัน (เทียบเพดาน).
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useDayoffOverview, useAdminSetDayoff, type DayoffOverviewGroup } from '../../hooks';
import { CalendarRange, Users, Briefcase, Pencil, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';

const TH_MON = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
function fmt(dateStr: string): string {
  const [, m, d] = dateStr.split('-').map(Number);
  return `${d} ${TH_MON[(m ?? 1) - 1]}`;
}
function eachDate(from: string, to: string): string[] {
  const out: string[] = [];
  const a = new Date(`${from}T00:00:00Z`), b = new Date(`${to}T00:00:00Z`);
  for (let t = a.getTime(); t <= b.getTime(); t += 86400000) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
}

function GroupCard({ g, days, periodEnd, holidayByDay }: { g: DayoffOverviewGroup; days: string[]; periodEnd: string; holidayByDay: Map<string, string> }) {
  // วันที่กำลังกางดู "ใครหยุด" — null = ยังไม่ได้เลือก
  const [openDay, setOpenDay] = useState<string | null>(null);

  // ใครหยุด / ใครมาทำงาน ในวันที่เลือก (คิดจากข้อมูลรายคนที่มีอยู่แล้ว)
  const holidayName = openDay ? holidayByDay.get(openDay) : undefined;
  const offMembers = openDay
    ? (holidayName
        // วันนักขัตฯ = หยุดทุกคน ยกเว้นคนที่กด "มาทำงาน"
        ? g.members.filter((m) => !(m.holiday_work_dates ?? []).includes(openDay))
        // วันปกติ = คนที่ลงวันหยุดวันนั้น
        : g.members.filter((m) => m.dates.includes(openDay)))
    : [];
  const workMembers = openDay && holidayName
    ? g.members.filter((m) => (m.holiday_work_dates ?? []).includes(openDay))
    : [];

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Briefcase className="w-4 h-4 text-brand-600 shrink-0" />
          <span className="font-semibold truncate">{g.position}</span>
          <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 shrink-0">
            <Users className="w-3 h-3" />{g.headcount}
          </span>
        </div>
        <span className="text-xs text-gray-500 shrink-0">
          {g.cap > 0 ? `เพดาน ${g.cap}/วัน${g.manual_cap ? '' : ' (auto)'}` : 'ไม่จำกัด'}
        </span>
      </div>

      {/* แถบจำนวนคนหยุดต่อวัน (เลื่อนแนวนอน) — วันเต็ม = แดง · แตะช่องวันเพื่อดูว่าใครหยุด */}
      <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-1 w-max">
          {days.map((d) => {
            const c = g.day_counts[d] ?? 0;
            const full = g.cap > 0 && c >= g.cap;
            const holiday = holidayByDay.get(d);
            const selected = openDay === d;
            return (
              <button key={d} type="button"
                onClick={() => setOpenDay((cur) => (cur === d ? null : d))}
                title={holiday ? `วันหยุดนักขัตฤกษ์: ${holiday}` : 'แตะเพื่อดูว่าใครหยุด'}
                className={`shrink-0 w-10 rounded-md text-center py-1 transition-shadow ${
                selected ? 'ring-2 ring-brand-500 ' : ''
              }${
                holiday ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-200'
                  : c === 0 ? 'bg-gray-50 text-gray-300'
                  : full ? 'bg-red-100 text-red-700 font-semibold'
                  : 'bg-brand-50 text-brand-700'
              }`}>
                <div className="text-[10px] leading-none">{fmt(d).split(' ')[0]}</div>
                <div className="text-[11px] leading-tight">{holiday ? 'หยุด' : `${c}${g.cap > 0 ? `/${g.cap}` : ''}`}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* กางรายชื่อ "ใครหยุดวันนี้" เมื่อแตะช่องวัน — ช่วย HR เช็คว่าแผนกที่หยุดพร้อมกันไม่ได้ ชนกันไหม */}
      {openDay && (
        <div className="rounded-lg bg-gray-50 ring-1 ring-gray-100 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">
              {fmt(openDay)}{holidayName ? ` · วันหยุดนักขัตฤกษ์` : ''} — หยุด {offMembers.length} คน
            </span>
            <button onClick={() => setOpenDay(null)} className="text-gray-400 shrink-0" title="ปิด"><X className="w-4 h-4" /></button>
          </div>
          {offMembers.length === 0 ? (
            <div className="text-xs text-gray-400">ไม่มีใครหยุดวันนี้</div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {offMembers.map((m) => (
                <span key={m.emp_code} className="text-[11px] bg-white ring-1 ring-gray-200 rounded-full px-2 py-0.5">{m.name}</span>
              ))}
            </div>
          )}
          {/* วันนักขัตฯ: คนที่กด "มาทำงาน" (ไม่ได้หยุด) — แยกให้ HR เห็นว่ามีคนคุมงานไหม */}
          {holidayName && workMembers.length > 0 && (
            <div className="pt-1">
              <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700">
                <Briefcase className="w-3 h-3" /> มาทำงาน {workMembers.length} คน:
              </span>
              <div className="flex flex-wrap gap-1 mt-1">
                {workMembers.map((m) => (
                  <span key={m.emp_code} className="text-[11px] bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">{m.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* รายชื่อ + วันที่จอง (HR แก้ไขรายคนได้ แม้ปิดรอบแล้ว) */}
      <div className="divide-y divide-gray-100">
        {g.members.map((m) => (
          <MemberRow key={m.emp_code} m={m} days={days} quota={g.quota} periodEnd={periodEnd} />
        ))}
      </div>
      {g.quota == null && <div className="text-[11px] text-amber-600">แผนกยังไม่ตั้งโควตา</div>}
    </div>
  );
}

type Member = DayoffOverviewGroup['members'][number];

function MemberRow({ m, days, quota, periodEnd }: { m: Member; days: string[]; quota: number | null; periodEnd: string }) {
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState<string[]>(m.dates);
  const setDayoff = useAdminSetDayoff();
  const toggle = (d: string) => setSel((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d].sort()));
  const over = quota != null && sel.length > quota;
  const save = (force = false) => setDayoff.mutate(
    { emp_code: m.emp_code, dates: sel, period_end: periodEnd, force }, // ผูกกับรอบที่กำลังดู → แก้รอบปัจจุบัน/ย้อนหลังได้
    {
      onSuccess: () => setEditing(false),
      onError: (e: unknown) => {
        const msg = (e as Error)?.message || 'บันทึกไม่สำเร็จ';
        // กรณีสลับวันหยุด 2 คน จะติดเพดานเสมอ (อีกคนยังถือวันเดิม) → ให้ HR ยืนยันบันทึกทับได้
        if (!force && /เพดาน/.test(msg)) {
          if (confirm(`${msg}\n\nถ้ากำลัง "สลับวันหยุด" ระหว่าง 2 คน กดตกลงเพื่อบันทึกทับ — เพดานจะกลับมาถูกต้องเมื่อบันทึกอีกคนเสร็จ`)) {
            save(true);
          }
        } else {
          alert(msg);
        }
      },
    },
  );

  return (
    <div className="py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium min-w-0 truncate">{m.name}</div>
        {editing ? (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => save()} disabled={over || setDayoff.isPending} className="inline-flex items-center gap-0.5 text-[11px] text-white bg-brand-600 disabled:opacity-40 rounded-full px-2 py-1">
              <Check className="w-3 h-3" /> บันทึก
            </button>
            <button onClick={() => { setSel(m.dates); setEditing(false); }} className="inline-flex items-center text-[11px] text-gray-500 rounded-full px-1.5 py-1">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button onClick={() => { setSel(m.dates); setEditing(true); }} className="inline-flex items-center gap-0.5 text-[11px] text-brand-600 shrink-0">
            <Pencil className="w-3 h-3" /> แก้ไข
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-1.5">
          <div className="flex flex-wrap gap-1">
            {days.map((d) => {
              const on = sel.includes(d);
              return (
                <button key={d} onClick={() => toggle(d)} className={`text-[11px] rounded-full px-2 py-0.5 border ${on ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-200'}`}>
                  {fmt(d)}
                </button>
              );
            })}
          </div>
          <div className={`text-[11px] mt-1 ${over ? 'text-red-600' : 'text-gray-400'}`}>
            เลือก {sel.length} วัน{quota != null ? ` / โควตา ${quota}` : ''}{over ? ' — เกินโควตา' : ''}
          </div>
        </div>
      ) : m.dates.length === 0 ? (
        <div className="text-xs text-gray-400 mt-0.5">ยังไม่ลง</div>
      ) : (
        <div className="flex flex-wrap gap-1 mt-1">
          {m.dates.map((d) => (
            <span key={d} className="text-[11px] bg-gray-100 rounded-full px-2 py-0.5">{fmt(d)}</span>
          ))}
        </div>
      )}

      {/* วันนักขัตฯ ที่กด "มาทำงาน" (จ่าย 2 เท่าเมื่อเช็คอินจริง) — ต่างจากวันหยุดปกติ */}
      {(m.holiday_work_dates?.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-1 mt-1">
          <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700">
            <Briefcase className="w-3 h-3" /> มาทำงานวันนักขัตฯ:
          </span>
          {m.holiday_work_dates!.map((d) => (
            <span key={d} className="text-[11px] bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">{fmt(d)}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export function HRDayoffOverview() {
  const [offset, setOffset] = useState(0);
  const { data, isLoading } = useDayoffOverview(offset);
  const holidayByDay = new Map<string, string>();
  for (const h of data?.holidays ?? []) holidayByDay.set(h.date, h.name);

  return (
    <AdminLayout title="ภาพรวมวันหยุด">
      {isLoading || !data ? (
        <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
      ) : (
        <div className="space-y-4">
          <div className="card p-4 flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-brand-600 shrink-0" />
            <div className="text-sm flex-1 min-w-0">
              <div className="font-semibold">รอบ {fmt(data.cycle.start)} – {fmt(data.cycle.end)}{offset === 0 ? ' (รอบนี้)' : ''}</div>
              <div className="text-gray-500 text-xs">ลงวันหยุดแล้ว {data.booked_count} คน</div>
              {(data.holidays?.length ?? 0) > 0 && (
                <div className="text-violet-600 text-xs mt-0.5">
                  วันนักขัตฤกษ์ (หยุดทุกคน): {data.holidays!.map((h) => fmt(h.date)).join(', ')}
                </div>
              )}
            </div>
            {/* เลือกดูรอบอื่น */}
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => setOffset((o) => Math.max(-12, o - 1))} disabled={offset <= -12} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-30" title="รอบก่อนหน้า"><ChevronLeft className="w-4 h-4" /></button>
              {offset !== 0 && <button onClick={() => setOffset(0)} className="text-xs text-brand-600 px-1">รอบนี้</button>}
              <button onClick={() => setOffset((o) => Math.min(12, o + 1))} disabled={offset >= 12} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-30" title="รอบถัดไป"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>

          {data.groups.length === 0 && (
            <p className="py-6 text-center text-gray-500 text-sm">ยังไม่มีการจองในรอบนี้</p>
          )}

          {data.groups.map((g) => (
            <GroupCard key={g.position} g={g} days={eachDate(data.cycle.start, data.cycle.end)} periodEnd={data.cycle.end} holidayByDay={holidayByDay} />
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
